import React, { useEffect } from 'react';
import { AuthPlatform, StoredTokens } from '../types';
import VKIconSvg from '../assets/vk.svg';
import MaxIcon, { MAX_COLOR } from './MaxIcon';

/**
 * Здесь две разные роли, и раньше они были склеены в одну:
 *
 * BASE — аккаунт, К КОТОРОМУ подключают. Годится любая живая сессия, включая
 * почту и Яндекс: человек, зарегистрировавшийся по коду с почты, приходит сюда
 * именно чтобы прицепить к своему профилю Telegram.
 *
 * LINKABLE — что реально умеет прицепляться. Ручки connect есть только у ВК и
 * Telegram, поэтому в «Подключить» показываем только их.
 */
type Provider = AuthPlatform;
type LinkableProvider = 'vk' | 'telegram';

const BASE_PROVIDERS: Provider[] = ['vk', 'telegram', 'max', 'yandex', 'email'];
const LINKABLE_PROVIDERS: LinkableProvider[] = ['vk', 'telegram'];
/** Чем можно войти прямо отсюда, если сессий ещё нет. Почта = вход по коду. */
const LOGIN_PROVIDERS: Provider[] = ['telegram', 'max', 'vk', 'email'];

const isLinkable = (provider: Provider): provider is LinkableProvider =>
  (LINKABLE_PROVIDERS as Provider[]).includes(provider);

interface AccountLinkingPageProps {
  storedTokensData: StoredTokens;
  /** Выбранный главный аккаунт (состояние живёт в App и переживает редиректы). */
  primary: Provider | null;
  onSelectPrimary: (provider: Provider) => void;
  onConnectProvider: (provider: LinkableProvider) => void;
  onBack: () => void;
  onLoginProvider: (provider: Provider) => void;
  isLoading?: boolean;
}

const AccountLinkingPage: React.FC<AccountLinkingPageProps> = ({
  storedTokensData,
  primary,
  onSelectPrimary,
  onConnectProvider,
  onBack,
  onLoginProvider,
  isLoading = false,
}) => {
  // Определяем залогиненные аккаунты (любая сессия годится как главный аккаунт)
  const loggedInProviders = BASE_PROVIDERS.filter((provider) => {
    const data = storedTokensData[provider];
    return Boolean(data) && !data?.isExpired;
  });

  const hasMultipleAccounts = loggedInProviders.length > 1;
  const hasSingleAccount = loggedInProviders.length === 1;
  const hasNoAccounts = loggedInProviders.length === 0;

  const selectedPrimary = primary;
  const soleProvider = hasSingleAccount ? loggedInProviders[0] : null;

  // Единственный аккаунт главный по определению — сообщаем наверх, иначе связывание
  // уйдёт от произвольного токена.
  useEffect(() => {
    if (soleProvider && primary !== soleProvider) {
      onSelectPrimary(soleProvider);
    }
  }, [soleProvider, primary, onSelectPrimary]);

  // Провайдеры, доступные для подключения
  const availableToConnect = LINKABLE_PROVIDERS.filter(
    p => !loggedInProviders.includes(p)
  );

  // Проверяем, объединены ли уже аккаунты
  const primaryData = selectedPrimary ? storedTokensData[selectedPrimary] : null;
  const hasUnifiedAccount = primaryData?.backendUser?.connection?.vk && 
                           primaryData?.backendUser?.connection?.telegram;

  const getProviderName = (provider: Provider) => {
    switch (provider) {
      case 'vk': return 'ВКонтакте';
      case 'telegram': return 'Telegram';
      case 'max': return 'MAX';
      case 'yandex': return 'Яндекс ID';
      case 'email': return 'Почта';
    }
  };

  /** Подпись кнопки входа: у почты вход не «через почту», а по коду с неё. */
  const getLoginLabel = (provider: Provider) => {
    switch (provider) {
      case 'email': return 'Войти по коду с почты';
      case 'yandex': return 'Войти через Яндекс ID';
      default: return `Войти через ${getProviderName(provider)}`;
    }
  };

  const getProviderColor = (provider: Provider) => {
    switch (provider) {
      case 'vk': return '#0077FF';
      case 'telegram': return '#27A7E7';
      case 'max': return MAX_COLOR;
      case 'yandex': return '#FC3F1D';
      case 'email': return '#8B5CF6';
    }
  };

  const getProviderIcon = (provider: Provider) => {
    switch (provider) {
      case 'vk':
        return (
          <div className="w-10 h-10 rounded-xl bg-[#0077FF] flex items-center justify-center flex-shrink-0">
            <img src={VKIconSvg} alt="VK" className="w-auto h-5" />
          </div>
        );
      case 'telegram':
        return (
          <div className="w-10 h-10 rounded-xl bg-[#27A7E7] flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-on-accent" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
          </div>
        );
      case 'max':
        return <MaxIcon />;
      case 'yandex':
        return (
          <div className="w-10 h-10 rounded-xl bg-[#FC3F1D] flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-semibold leading-none text-on-accent">Я</span>
          </div>
        );
      case 'email':
        return (
          <div className="w-10 h-10 rounded-xl bg-[#8B5CF6] flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-on-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
        );
    }
  };

  const handleConnectClick = (provider: LinkableProvider) => {
    if (selectedPrimary) {
      onConnectProvider(provider);
    }
  };

  const handlePrimarySelect = (provider: Provider) => {
    onSelectPrimary(provider);
  };

  // Если нет аккаунтов - предлагаем войти
  if (hasNoAccounts) {
    return (
      <div className="flex flex-col min-h-screen bg-zinc-900">
        {/* Header */}
        <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
          <button
            onClick={onBack}
            className="flex items-center text-zinc-500 hover:text-zinc-300 transition-colors mb-6"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm">Назад</span>
          </button>
          
          <div className="flex items-center justify-center mb-2">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
          </div>
          <h1 className="text-xl font-semibold text-center text-white">
            Связать аккаунты
          </h1>
          <p className="mt-1 text-sm text-center text-zinc-500">
            Сначала войдите в систему
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 py-6">
          <p className="text-sm text-zinc-400 text-center mb-6">
            Для связывания аккаунтов необходимо сначала авторизоваться
          </p>

          <div className="space-y-3">
            {LOGIN_PROVIDERS.map((provider) => (
              <button
                key={provider}
                onClick={() => onLoginProvider(provider)}
                className="w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800"
              >
                {getProviderIcon(provider)}
                <span className="ml-3 text-sm font-medium text-white">
                  {getLoginLabel(provider)}
                </span>
                <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-zinc-900">
      {/* Header */}
      <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="flex items-center text-zinc-500 hover:text-zinc-300 transition-colors mb-6 disabled:opacity-50"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Назад</span>
        </button>
        
        <div className="flex items-center justify-center mb-2">
          <div className="w-12 h-12 rounded-2xl bg-purple-500/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
        </div>
        <h1 className="text-xl font-semibold text-center text-white">
          Связать аккаунты
        </h1>
        <p className="mt-1 text-sm text-center text-zinc-500">
          {hasMultipleAccounts 
            ? 'Выберите главный аккаунт' 
            : 'Подключите дополнительные способы входа'
          }
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6">
        {/* Unified account notice */}
        {hasUnifiedAccount && (
          <div className="p-4 mb-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <div className="flex items-start">
              <svg className="flex-shrink-0 mr-2 w-5 h-5 text-emerald-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <p className="text-sm font-medium text-emerald-400">Аккаунты уже связаны</p>
                <p className="text-xs text-emerald-400/70 mt-1">
                  VK и Telegram подключены к единому профилю
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Primary account selection (when multiple accounts logged in) */}
        {hasMultipleAccounts && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-zinc-300 mb-3">
              Главный аккаунт
            </h3>
            <p className="text-xs text-zinc-500 mb-4">
              Выберите аккаунт, к которому будут привязаны остальные
            </p>
            
            <div className="space-y-2">
              {loggedInProviders.map((provider) => {
                const data = storedTokensData[provider];
                const isSelected = selectedPrimary === provider;
                
                return (
                  <button
                    key={provider}
                    onClick={() => handlePrimarySelect(provider)}
                    disabled={isLoading}
                    className={`w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 border ${
                      isSelected 
                        ? 'bg-purple-500/10 border-purple-500/50' 
                        : 'bg-zinc-800/50 border-zinc-700/50 hover:bg-zinc-800'
                    } disabled:opacity-50`}
                  >
                    {/* Radio indicator */}
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 ${
                      isSelected ? 'border-purple-500 bg-purple-500' : 'border-zinc-600'
                    }`}>
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                    
                    {/* User avatar */}
                    <div className="overflow-hidden flex-shrink-0 w-10 h-10 rounded-xl bg-zinc-800">
                      {data?.user?.avatar ? (
                        <img src={data.user.avatar} alt="" className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                          {data?.user?.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 ml-3 min-w-0 text-left">
                      <div className="text-sm font-medium text-white truncate">
                        {data?.user?.name || 'Пользователь'}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {getProviderName(provider)}
                      </div>
                    </div>
                    
                    {isSelected && (
                      <span className="text-xs font-medium text-purple-400 ml-2">
                        Главный
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Single account display */}
        {hasSingleAccount && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-zinc-300 mb-3">
              Текущий аккаунт
            </h3>
            
            {loggedInProviders.map((provider) => {
              const data = storedTokensData[provider];
              
              return (
                <div
                  key={provider}
                  className="flex items-center px-4 py-3.5 rounded-xl bg-zinc-800/50 border border-zinc-700/50"
                >
                  {/* User avatar */}
                  <div className="overflow-hidden flex-shrink-0 w-10 h-10 rounded-xl bg-zinc-800">
                    {data?.user?.avatar ? (
                      <img src={data.user.avatar} alt="" className="object-cover w-full h-full" />
                    ) : (
                      <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                        {data?.user?.name?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 ml-3 min-w-0 text-left">
                    <div className="text-sm font-medium text-white truncate">
                      {data?.user?.name || 'Пользователь'}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {getProviderName(provider)}
                    </div>
                  </div>
                  
                  <span className="text-xs font-medium text-purple-400">
                    Главный
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Divider */}
        {availableToConnect.length > 0 && (
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-800"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-zinc-900 text-xs text-zinc-500">
                Подключить
              </span>
            </div>
          </div>
        )}

        {/* Connect buttons */}
        {availableToConnect.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs text-zinc-500 mb-3">
              {hasMultipleAccounts && !selectedPrimary
                ? 'Сначала выберите главный аккаунт'
                : 'Добавьте дополнительные способы входа'
              }
            </p>
            
            {availableToConnect.map((provider) => {
              const color = getProviderColor(provider);
              const disabled = isLoading || (hasMultipleAccounts && !selectedPrimary);
              
              return (
                <button
                  key={provider}
                  onClick={() => handleConnectClick(provider)}
                  disabled={disabled}
                  style={{
                    backgroundColor: disabled ? undefined : `${color}10`,
                    borderColor: disabled ? undefined : `${color}30`,
                  }}
                  className={`w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 border ${
                    disabled
                      ? 'bg-zinc-800/30 border-zinc-700/30 opacity-50 cursor-not-allowed'
                      : 'hover:opacity-80'
                  }`}
                >
                  {getProviderIcon(provider)}
                  <span className="ml-3 text-sm font-medium text-white">
                    Подключить {getProviderName(provider)}
                  </span>
                  <svg className="ml-auto w-4 h-4 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              );
            })}
          </div>
        )}

        {/* Already logged in providers that can be connected */}
        {hasMultipleAccounts && selectedPrimary && (
          <div className="mt-6">
            <p className="text-xs text-zinc-500 mb-3">
              Связать с главным аккаунтом
            </p>
            
            <div className="space-y-2">
              {loggedInProviders
                .filter((p): p is LinkableProvider => p !== selectedPrimary && isLinkable(p))
                .map((provider) => {
                  const data = storedTokensData[provider];
                  return (
                    <button
                      key={provider}
                      onClick={() => handleConnectClick(provider)}
                      disabled={isLoading}
                      className="w-full flex items-center px-4 py-3.5 rounded-xl transition-all duration-200 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 disabled:opacity-50"
                    >
                      {/* User avatar */}
                      <div className="overflow-hidden flex-shrink-0 w-10 h-10 rounded-xl bg-zinc-800">
                        {data?.user?.avatar ? (
                          <img src={data.user.avatar} alt="" className="object-cover w-full h-full" />
                        ) : (
                          <div className="flex justify-center items-center w-full h-full font-medium text-zinc-400">
                            {data?.user?.name?.charAt(0) || '?'}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 ml-3 min-w-0 text-left">
                        <div className="text-sm font-medium text-white truncate">
                          {data?.user?.name || 'Пользователь'}
                        </div>
                        <div className="text-xs text-zinc-500">
                          {getProviderName(provider)}
                        </div>
                      </div>
                      
                      <div className="flex items-center text-emerald-400">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                        </svg>
                        <span className="text-xs font-medium">Связать</span>
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {/* Info note */}
        <div className="mt-6 p-3 rounded-xl bg-zinc-900 border border-zinc-800">
          <p className="text-xs text-zinc-500 text-center">
            После связывания вы сможете входить через любой из подключенных аккаунтов
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-zinc-800/50">
        <p className="text-xs text-center text-zinc-600">
          Все данные сохраняются в одном профиле
        </p>
      </div>
    </div>
  );
};

export default AccountLinkingPage;
